import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import ProductCard from "@/components/ProductCard";
import { Filter } from "lucide-react";
import Seo from "@/components/Seo";

const COLLECTIONS = [
  { value: "fresh", label: "Fresh Arrangements" },
  { value: "preserved", label: "Preserved Arrangements" },
];

const FLOWER_FILTERS = [
  "Roses", "Hydrangeas", "Peonies", "Mixed Florals", "Signature Designs", "Luxury Arrangements", "Thought of You",
];

export default function Collection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const collectionType = searchParams.get("type") === "preserved" ? "preserved" : "fresh";
  const [activeTag, setActiveTag] = useState(searchParams.get("tag") || "");

  useEffect(() => {
    setActiveTag(searchParams.get("tag") || "");
  }, [searchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data.filter((p) => p.category !== "event"));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const setCollection = (type) => {
    setActiveTag("");
    setSearchParams(type === "preserved" ? { type: "preserved" } : {});
  };

  const visible = useMemo(() => {
    return products.filter((p) => {
      const ptype = p.collection_type || "fresh";
      if (ptype !== collectionType) return false;
      if (activeTag && !(p.flower_tags || []).includes(activeTag)) return false;
      return true;
    });
  }, [products, collectionType, activeTag]);

  // Only show filter chips that exist in the current collection
  const availableTags = useMemo(() => {
    const present = new Set();
    products
      .filter((p) => (p.collection_type || "fresh") === collectionType)
      .forEach((p) => (p.flower_tags || []).forEach((t) => present.add(t)));
    return FLOWER_FILTERS.filter((t) => present.has(t));
  }, [products, collectionType]);

  return (
    <div className="page-transition pt-[72px]" data-testid="collection-page">
      <Seo
        title="Luxury Flower Delivery & Rose Bouquets in Minneapolis | Fleuris Noir"
        description="Browse luxury rose bouquets from 25 to 150 roses, signature arrangements and gifts from Fleuris Noir. Premium Ecuadorian roses delivered across Minneapolis and the Twin Cities."
        path="/collection"
        jsonLd={{
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://fleurisnoir.com/" },
            { "@type": "ListItem", position: 2, name: "Collection", item: "https://fleurisnoir.com/collection" },
          ],
        }}
      />
      {/* Header */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <p className="overline mb-4">Our Creations</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#f8f5f0] mb-4">The Collection</h1>
          <p className="font-body text-[#a19d98] max-w-2xl mx-auto">
            Each arrangement is thoughtfully composed to embody our signature aesthetic while celebrating the natural beauty of blooms.
          </p>
        </div>
      </section>

      {/* Primary collection tabs */}
      <section className="py-10 bg-[#0c0c0c]">
        <div className="container-custom">
          <div className="grid grid-cols-2 gap-3 max-w-xl mx-auto" data-testid="collection-tabs">
            {COLLECTIONS.map((c) => (
              <button
                key={c.value}
                onClick={() => setCollection(c.value)}
                className={`py-5 font-heading text-base md:text-lg border transition-all ${
                  collectionType === c.value
                    ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                    : "bg-transparent border-[#b8966a]/25 text-[#a19d98] hover:border-[#b8966a]/60"
                }`}
                data-testid={`collection-tab-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Flower filters */}
      {availableTags.length > 0 && (
        <section className="py-6 bg-[#161616] border-y border-[#b8966a]/10 sticky top-[72px] z-40">
          <div className="container-custom">
            <div className="flex items-center gap-3 overflow-x-auto pb-1">
              <Filter size={16} className="text-[#b8966a] flex-shrink-0" />
              <button
                onClick={() => setActiveTag("")}
                className={`font-sans text-xs tracking-[0.15em] uppercase whitespace-nowrap px-4 py-2 border transition-all ${
                  activeTag === ""
                    ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                    : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
                }`}
                data-testid="filter-all"
              >
                All
              </button>
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`font-sans text-xs tracking-[0.15em] uppercase whitespace-nowrap px-4 py-2 border transition-all ${
                    activeTag === tag
                      ? "bg-[#b8966a] border-[#b8966a] text-[#0c0c0c]"
                      : "bg-transparent border-[#b8966a]/20 text-[#a19d98] hover:border-[#b8966a]/50"
                  }`}
                  data-testid={`filter-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products Grid */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-[#161616] animate-pulse">
                  <div className="aspect-[3/4] bg-[#1f1f1f]" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-20 bg-[#1f1f1f]" />
                    <div className="h-6 w-32 bg-[#1f1f1f]" />
                    <div className="h-4 w-full bg-[#1f1f1f]" />
                  </div>
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-20" data-testid="collection-empty">
              <p className="font-body text-[#a19d98]">
                {collectionType === "preserved"
                  ? "Our preserved arrangements are being composed. Please check back soon — or inquire for a bespoke preserved piece."
                  : "No arrangements found for this filter."}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((product, index) => (
                <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
